import { useEffect, useState } from 'react';
import { Configuration, OrganizationsApi } from '@/api';
import {
  invalidateOrganizationListQuery,
  organizationListQueryKey,
} from '@/api/queries/organizationQueries';
import { Organization, Organizations } from '@/models/Organization';
import { EmptyResult } from '@/models/Pagination';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { setPaginationResultIfNotLoading } from '@/utils/pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<Organization>();

interface UseFindOrganizationsQueryResult {
  organizations: Organizations;
  isLoading: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

const useFindOrganizationsQuery = (
  page: number,
  searchQuery?: string,
): UseFindOrganizationsQueryResult => {
  const queryClient = useQueryClient();
  const [organizations, setOrganizations] = useState<Organizations>(initialState);

  const organizationsApi = new OrganizationsApi(new Configuration(useAuth));

  const itemsPerPage = 10;

  const { isLoading, data, isFetching, error } = useQuery({
    queryKey: [organizationListQueryKey, page, searchQuery],
    queryFn: () =>
      organizationsApi.findOrganizations(
        searchQuery,
        page * itemsPerPage - itemsPerPage,
        itemsPerPage,
      ),
  });

  useEffect(() => {
    setPaginationResultIfNotLoading(data, setOrganizations, isLoading);
  }, [data, isLoading, isFetching]);

  const invalidateQuery = async () => {
    return invalidateOrganizationListQuery(queryClient);
  };

  return {
    organizations,
    isLoading,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindOrganizationsQuery;
